import React, { FC, useEffect, useMemo, useState } from 'react'
import { Select } from 'antd'
import { useQuery } from '@apollo/client'
import { GetVendorsForLookup } from '../../contact/graphql/contact.queries.graphql'
import { ItemType } from '../../inventory/type/item'

interface Props {
  value?: number
  onChange: (next: number | undefined, label?: string) => void
  productType: ItemType
  disabled?: boolean
  placeholder?: string
}

// Page size for the lookup.
const PAGE_TAKE = 200

const labelFor = (v: { company?: string | null; designer?: string | null; contactId?: string | null; id: number }) => {
  const contactId = v.contactId?.trim()
  const company = v.company?.trim()
  if (contactId && company) return `${contactId} - ${company}`
  return contactId || company || v.designer?.trim() || `#${v.id}`
}

export const OverrideVendorSelect: FC<Props> = ({
  value,
  onChange,
  productType,
  disabled,
  placeholder,
}) => {
  const [search, setSearch] = useState<string>('')
  const [labelCache, setLabelCache] = useState<Record<number, string>>({})

  const { data, loading } = useQuery(GetVendorsForLookup, {
    variables: {
      request: {
        isVendor: true,
        productType,
        searchVendor: search || undefined,
        take: PAGE_TAKE,
        skip: 0,
      },
    },
    fetchPolicy: 'cache-and-network',
  })

  const allRows = useMemo(() => data?.contactsFind.data ?? [], [data])

  useEffect(() => {
    if (allRows.length === 0) return
    setLabelCache((prev) => {
      const next = { ...prev }
      for (const row of allRows) next[row.id] = labelFor(row)
      return next
    })
  }, [allRows])

  const filteredOptions = useMemo(
    () => allRows.map((row) => ({ value: row.id, label: labelFor(row) })),
    [allRows],
  )

  // Preserve the selected option's label across searches so AntD's Select
  // doesn't fall back to rendering the raw id.
  const options = useMemo(() => {
    if (value === undefined) return filteredOptions
    if (filteredOptions.some((o) => o.value === value)) return filteredOptions
    const cached = labelCache[value]
    return [{ value, label: cached ?? `#${value}` }, ...filteredOptions]
  }, [filteredOptions, value, labelCache])

  return (
    <Select
      showSearch
      allowClear
      placeholder={placeholder}
      loading={loading}
      disabled={disabled}
      value={value}
      filterOption={false}
      onSearch={setSearch}
      options={options}
      onChange={(nextValue) => {
        if (nextValue === undefined) {
          onChange(undefined, undefined)
          return
        }
        const label =
          allRows.find((row) => row.id === nextValue) && labelFor(allRows.find((row) => row.id === nextValue)!)
        onChange(nextValue, label || labelCache[nextValue])
      }}
      style={{ width: '100%' }}
    />
  )
}
